import { useState } from "react";
import {
  RiLineChartLine,
  RiMenuLine,
  RiDownloadCloud2Line,
  RiStore2Line,
  RiMegaphoneLine,
  RiTrophyLine,
} from "react-icons/ri";
import { BiBookOpen } from "react-icons/bi";
import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { useAuth } from "../../../hooks/AuthContext";
import { useOnboard } from "../../../hooks/OnboardContext";
import { i18n } from "../../../localization/i18n";
import { LockedFeatureIcon } from "../../LockedFeatureIcon";
import { MenuButton, MenuItemContainer } from "../styles";

interface MenuListProps {
  extendedMode: boolean;
  setExtendedMode?: (val: boolean) => void;
}

type LockedMenuKey = "solutions" | "orders" | "success";

export const FixiterMenuList = ({ extendedMode }: MenuListProps) => {
  const { user, hasProvider: authHasProvider } = useAuth();
  const { licensee, hasProvider: onboardHasProvider } = useOnboard();
  const location = useLocation();
  const [lockedModalItem, setLockedModalItem] = useState<LockedMenuKey | null>(null);

  const tooltipProps = {
    tooltip: {
      sx: {
        backgroundColor: "var(--dark-gray)",
        color: "var(--white)",
        fontSize: "14px",
        fontWeight: "500",
        padding: "8px 14px",
        borderRadius: "6px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
      },
    },
    arrow: {
      sx: {
        color: "var(--dark-gray)",
      },
    },
  };

  const hasProviderId = Boolean(
    onboardHasProvider ||
      authHasProvider ||
      licensee?.provider_id ||
      (licensee as any)?.provider?.id ||
      (user as any)?.person?.licensee?.provider_id ||
      (user as any)?.person?.licensee?.provider?.id ||
      (user as any)?.licensee?.provider_id ||
      (user as any)?.licensee?.provider?.id
  );

  const renderProviderGuardedItem = ({
    itemKey,
    to,
    label,
    icon,
    isActive,
  }: {
    itemKey: LockedMenuKey;
    to: string;
    label: string;
    icon: React.ReactNode;
    isActive: boolean;
  }) => {
    if (hasProviderId) {
      return (
        <MenuItemContainer>
          <Link to={to}>
            <Tooltip
              title={extendedMode ? "" : label}
              placement="right"
              arrow
              disableHoverListener={extendedMode}
              componentsProps={tooltipProps}
            >
              <span style={{ display: "block" }}>
                <MenuButton extendedMode={extendedMode} isActive={isActive}>
                  <div className="content">
                    {icon}
                    <span>{label}</span>
                  </div>
                </MenuButton>
              </span>
            </Tooltip>
          </Link>
        </MenuItemContainer>
      );
    }

    return (
      <MenuItemContainer>
        <Tooltip
          title={extendedMode ? "" : label}
          placement="right"
          arrow
          disableHoverListener={extendedMode}
          componentsProps={tooltipProps}
        >
          <span style={{ display: "block" }}>
            <MenuButton
              as="div"
              role="button"
              tabIndex={0}
              extendedMode={extendedMode}
              isActive={false}
              onClick={() => setLockedModalItem(itemKey)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setLockedModalItem(itemKey);
                }
              }}
            >
              <div className="content">
                {icon}
                <span>{label}</span>
              </div>
              <div className="lock-badge">
                <LockedFeatureIcon
                  size={28}
                  iconSize={14}
                  open={lockedModalItem === itemKey}
                  onOpenChange={(open) => setLockedModalItem(open ? itemKey : null)}
                />
              </div>
            </MenuButton>
          </span>
        </Tooltip>
      </MenuItemContainer>
    );
  };

  return (
    <>
      {/* 1. Relatórios */}
      <MenuItemContainer>
        <Link to="/dashboard">
          <Tooltip
            title={extendedMode ? "" : `${i18n.t("sidebar.reports") || "Relatórios"}`}
            placement="right"
            arrow
            disableHoverListener={extendedMode}
            componentsProps={tooltipProps}
          >
            <span style={{ display: "block" }}>
              <MenuButton
                extendedMode={extendedMode}
                isActive={
                  location.pathname === "/dashboard" ||
                  location.pathname === "/" ||
                  location.pathname.includes("/fixiter/dashboard")
                }
              >
                <div className="content">
                  <RiLineChartLine size={24} />
                  <span>{`${i18n.t("sidebar.reports") || "Relatórios"}`}</span>
                </div>
              </MenuButton>
            </span>
          </Tooltip>
        </Link>
      </MenuItemContainer>

      {/* 2. Loja (Exibido apenas para Fixiter sem provider) */}
      {!hasProviderId && (
        <MenuItemContainer>
          <a
            href="http://loja.usefixit.com.br/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
          >
            <Tooltip
              title={extendedMode ? "" : `${i18n.t("sidebar.store") || "Loja"}`}
              placement="right"
              arrow
              disableHoverListener={extendedMode}
              componentsProps={tooltipProps}
            >
              <span style={{ display: "block" }}>
                <MenuButton extendedMode={extendedMode}>
                  <div className="content">
                    <RiStore2Line size={24} />
                    <span>{`${i18n.t("sidebar.store") || "Loja"}`}</span>
                  </div>
                </MenuButton>
              </span>
            </Tooltip>
          </a>
        </MenuItemContainer>
      )}

      {/* 3. Soluções */}
      {renderProviderGuardedItem({
        itemKey: "solutions",
        to: "/solutions",
        label: `${i18n.t("sidebar.solutions") || "Soluções"}`,
        icon: <RiDownloadCloud2Line size={24} />,
        isActive: location.pathname.includes("/solutions"),
      })}

      {/* 4. Pedidos */}
      {renderProviderGuardedItem({
        itemKey: "orders",
        to: "/orders",
        label: `${i18n.t("sidebar.requests") || "Pedidos"}`,
        icon: <RiMenuLine size={24} />,
        isActive: location.pathname.includes("/orders"),
      })}

      {/* 5. Treinamentos */}
      <MenuItemContainer>
        <a
          href="https://usefixit.com.br/treinamento"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          <Tooltip
            title={extendedMode ? "" : `${i18n.t("sidebar.courses") || "Treinamentos"}`}
            placement="right"
            arrow
            disableHoverListener={extendedMode}
            componentsProps={tooltipProps}
          >
            <span style={{ display: "block" }}>
              <MenuButton
                extendedMode={extendedMode}
                isActive={location.pathname.includes("/fixiter/courses")}
              >
                <div className="content">
                  <BiBookOpen size={24} />
                  <span>{`${i18n.t("sidebar.courses") || "Treinamentos"}`}</span>
                </div>
              </MenuButton>
            </span>
          </Tooltip>
        </a>
      </MenuItemContainer>

      {/* 6. Marketing */}
      <MenuItemContainer>
        <a
          href="https://usefixit.com.br/kit-fixiter"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          <Tooltip
            title={extendedMode ? "" : `${i18n.t("sidebar.marketing") || "Marketing"}`}
            placement="right"
            arrow
            disableHoverListener={extendedMode}
            componentsProps={tooltipProps}
          >
            <span style={{ display: "block" }}>
              <MenuButton extendedMode={extendedMode}>
                <div className="content">
                  <RiMegaphoneLine size={24} />
                  <span>{`${i18n.t("sidebar.marketing") || "Marketing"}`}</span>
                </div>
              </MenuButton>
            </span>
          </Tooltip>
        </a>
      </MenuItemContainer>

      {/* 7. Sucesso (Exibido apenas para Fixiter sem provider) */}
      {!hasProviderId &&
        renderProviderGuardedItem({
          itemKey: "success",
          to: "/success",
          label: `${i18n.t("sidebar.success") || "Sucesso"}`,
          icon: <RiTrophyLine size={24} />,
          isActive: location.pathname.includes("/success"),
        })}
    </>
  );
};
